/**
 * El recorrido de permisos de la primera vez: uno por uno, cada uno con su para
 * que, y solo los que hacen falta en ESTE aparato y para ESTA persona. La hoja
 * es `components/PermissionsTour.tsx`; aqui vive lo que se puede probar sin
 * pantalla.
 *
 * NO SE PIDEN SOLOS AL ABRIR, y no es timidez: el cuadro del sistema exige un
 * toque. Safari en iPhone no lo enseña sin uno, y Chrome castiga a los sitios
 * que preguntan sin que nadie lo pida —primero con el aviso silencioso en la
 * barra de direcciones, despues bloqueandolos solo—. Lo que hacen las apps
 * grandes es lo mismo que aqui: su propia pantalla dice para que es, y un toque
 * abre el cuadro del sistema. Una pregunta con contexto se contesta que si; una
 * en frio, que no, y un "no" del sistema ya no lo puede volver a preguntar
 * ninguna app web.
 */

import { useSyncExternalStore } from 'react';
import type { DevicePermissions } from './device-permissions.js';

export type TourStep =
  /** Pedir los avisos al telefono. */
  | 'notifications'
  /** iPhone o iPad en Safari: los avisos solo llegan con la app instalada. */
  | 'install-for-notifications'
  /** Pedir la camara para escanear. */
  | 'camera';

export interface TourWants {
  /** A esta persona le llega algun aviso (`receivesNotices`) y no los apago. */
  notifications: boolean;
  /** En este aparato se escanea con la camara (`useCameraScanAvailable`). */
  camera: boolean;
}

/**
 * Los pasos, en orden: primero los avisos, que son la razon de instalar, y
 * despues la camara. Lo que ya esta dado, lo negado —eso ya no lo puede volver
 * a preguntar una app web; Configuracion trae la guia— y lo que el aparato no
 * tiene, no se pregunta.
 */
export function planTour(permissions: DevicePermissions, wants: TourWants): TourStep[] {
  const steps: TourStep[] = [];
  if (wants.notifications) {
    if (permissions.notifications === 'pending') steps.push('notifications');
    else if (permissions.notifications === 'needs-install') steps.push('install-for-notifications');
  }
  if (wants.camera && permissions.camera === 'pending') steps.push('camera');
  return steps;
}

/*
 * "Ya se le mostro" es por persona y por aparato. Por aparato porque los
 * permisos son del aparato; por persona porque el dueño que entra en el telefono
 * donde antes entro un empleado —a quien no se le pidieron avisos, que no
 * recibe— todavia no ha visto la pregunta que a el si le toca.
 */
const keyFor = (userId: string): string => `abarrotes.permissions-tour.${userId}`;

export function tourSeen(userId: string): boolean {
  try {
    return localStorage.getItem(keyFor(userId)) === '1';
  } catch {
    // Sin almacenamiento no hay como recordarlo: se ofrece, y "Ahora no" basta.
    return false;
  }
}

export function markTourSeen(userId: string): void {
  try {
    localStorage.setItem(keyFor(userId), '1');
  } catch {
    // Sin almacenamiento se volvera a ofrecer la proxima vez: solo eso se pierde.
  }
}

// ---------------------------------------------------------------------------
// Turno con el instalador
// ---------------------------------------------------------------------------

/*
 * Mientras el recorrido esta en curso —aunque se aparte un momento para dejar
 * ver el instalador que el mismo abrio—, el instalador no se ofrece solo. Dos
 * hojas encimadas al abrir la app se cierran las dos sin leer.
 */
let active = false;
const listeners = new Set<() => void>();

export function setTourActive(next: boolean): void {
  if (next === active) return;
  active = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTourActive(): boolean {
  return useSyncExternalStore(subscribe, () => active, () => false);
}
